"use client";

import { truncateAddress } from "@aptos-labs/ts-sdk";
import { IconDeviceMobile, IconGlobe, IconSignOut } from "@decibeltrade/react-icons";
import { Button, IconButton } from "@decibeltrade/web-core/components";
import { focusRing } from "@decibeltrade/web-core/recipes";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback } from "react";
import { css, cva, cx } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { openLanguageSwitcherModal } from "@/components/modal/open-language-switcher-modal";
import { openPwaInstallModal } from "@/components/modal/pwa-install-modal";
import { openWalletSelectorModal } from "@/components/modal/wallet-selector-modal";
import { useAnalytics, useAnalyticsRef } from "@/contexts/analytics-context";
import { usePwaInstall } from "@/contexts/pwa-install-context";
import { useViewAs } from "@/contexts/view-as-context";
import { useAuthState } from "@/hooks/use-auth-state";
import { useLogout } from "@/hooks/use-logout";

import { Clickable } from "../clickable";
import { DrawerCloseButton } from "../drawer";
import { EncryptionIndicator } from "../encryption-indicator";
import { openViewAsModal } from "../modal/view-as-modal";
import { DecibelLogoWordmark } from "../vectors/decibel-logo-wordmark";
import { flattenNavItems, getIsPathActive, useNavItems } from "./nav-items";
import { NewBadge } from "./new-badge";
import { RewardsCountBadge } from "./rewards-count-badge";

export interface MobileNavDrawerProps {
  close: () => void;
}

export function MobileNavDrawer({ close }: MobileNavDrawerProps) {
  const t = useTranslations("navItems");
  const tFooter = useTranslations("footer");
  const tPwa = useTranslations("pwaInstall");
  const tWallet = useTranslations("walletSelector");
  const pathname = usePathname();
  const navItems = useNavItems();
  const { isViewAsAllowed } = useViewAs();
  const analytics = useAnalytics();
  const analyticsRef = useAnalyticsRef();
  const { installed, inAppBrowser, platform, iosSafari } = usePwaInstall();
  const canShowInstall = !installed && !inAppBrowser && (platform !== "ios" || iosSafari);
  const { state, subaccountAddress } = useAuthState();
  const logout = useLogout();

  const handleLanguageClick = useCallback(() => {
    close();
    openLanguageSwitcherModal();
  }, [close]);

  const handleCardVaultNavClick = useCallback(() => {
    analytics.cardVault.navTabClicked();
    close();
  }, [analytics, close]);

  const handleViewAsClick = useCallback(() => {
    close();
    openViewAsModal();
  }, [close]);

  const handleInstallClick = useCallback(() => {
    analyticsRef.current.pwa.drawerCtaClicked();
    close();
    openPwaInstallModal();
  }, [analyticsRef, close]);

  const handleDisconnectClick = useCallback(() => {
    logout();
    close();
  }, [logout, close]);

  const handleConnectWalletClick = useCallback(() => {
    close();
    openWalletSelectorModal();
  }, [close]);

  return (
    <div
      className={stack({
        position: "relative",
        bg: "background.primary",
        pt: "20",
        minW: "[min(312px,100vw)]",
        h: "full",
        gap: "0",
      })}
    >
      <DrawerCloseButton close={close} />
      <div className={css({ position: "absolute", top: "[22px]", right: "24" })}>
        <IconButton
          size="lg"
          variant="text"
          onClick={handleLanguageClick}
          ariaLabel={tFooter("changeLanguage")}
          data-testid="mobile-nav-language-button"
          className={css({ p: "12!", m: "-12" })}
        >
          <IconGlobe className={css({ w: "20", h: "20" })} />
        </IconButton>
      </div>
      <Link href="/" className={cx(focusRing(), css({ alignSelf: "center" }))} onClick={close}>
        <DecibelLogoWordmark className={css({ color: "white" })} />
      </Link>
      <div className={navListStyles}>
        {flattenNavItems(navItems).map((item, index) => {
          const isActive = getIsPathActive(pathname, item.path);
          const isCardVault = item.tx === "cardVault";
          return (
            <Link
              key={index}
              target={item.newTab ? "_blank" : undefined}
              className={cx(
                linkStyles({ variant: isActive ? "active" : "inactive" }),
                focusRing(),
                item.desktopOnly && desktopOnlyStyles,
              )}
              href={item.path}
              onClick={isCardVault ? handleCardVaultNavClick : close}
            >
              {t(item.tx)}
              {item.badge === "new" && <NewBadge label={t("newBadge")} />}
              {item.tx === "rewards" && <RewardsCountBadge />}
            </Link>
          );
        })}
        {isViewAsAllowed && (
          <Clickable
            onClick={handleViewAsClick}
            className={cx(
              linkStyles({ variant: "inactive" }),
              focusRing(),
              css({ textAlign: "left" }),
            )}
          >
            {t("viewAsUser")}
          </Clickable>
        )}
        {(state === "WALLET_CONNECTED" || state === "SESSION_KEY_IMPORTED") && (
          <Clickable
            onClick={handleDisconnectClick}
            aria-label={tWallet("disconnect")}
            className={cx(walletBlockStyles, focusRing())}
          >
            <div className={flex({ align: "center", gap: "8" })}>
              <IconSignOut className={css({ color: "icon.tertiary", w: "20", h: "20" })} />
              <span className={css({ textStyle: "subheading.xs", color: "text.tertiary" })}>
                {tWallet("disconnect")}
              </span>
            </div>
            <span className={css({ textStyle: "subheading.xs", color: "text.disabled" })}>
              {truncateAddress(subaccountAddress)}
            </span>
          </Clickable>
        )}
        {state === "DISCONNECTED" && (
          <Button
            variant="secondary"
            onClick={handleConnectWalletClick}
            className={css({ flexShrink: 0, my: "12" })}
          >
            {tWallet("connectWallet")}
          </Button>
        )}
        {canShowInstall && (
          <Button
            variant="brand"
            onClick={handleInstallClick}
            className={css({ flexShrink: 0, my: "12" })}
            LeadIcon={IconDeviceMobile}
          >
            {tPwa("drawerCta")}
          </Button>
        )}
      </div>
      <div className={bottomBarStyles}>
        <EncryptionIndicator onOpen={close} />
      </div>
    </div>
  );
}

const navListStyles = stack({
  mt: "20",
  gap: "0",
  px: "24",
  pt: "8",
  pb: "8",
  flex: "1",
  minH: "0",
  overflow: "auto",
});

// Empty when the device is outside the encryption rollout; keeps only the safe-area inset then.
const bottomBarStyles = css({
  display: "flex",
  justifyContent: "center",
  flexShrink: 0,
  px: "24",
  pt: "8",
  pb: "[max(env(safe-area-inset-bottom, 0px), 12px)]",
  _empty: { pt: "0", pb: "[env(safe-area-inset-bottom, 0px)]" },
});

const walletBlockStyles = stack({
  align: "flex-start",
  gap: "4",
  py: "12",
  cursor: "pointer",
  flexShrink: 0,
  textAlign: "left",
});

const desktopOnlyStyles = css({ hideBelow: "desktop" });

const linkStyles = cva({
  base: {
    display: "flex",
    alignItems: "center",
    py: "12",
    cursor: "pointer",
    textDecoration: "none",
    textStyle: "subheading.xs",
    color: "text.tertiary",
    transitionPreset: "colors",
    _hover: {
      color: "text.primary",
    },
  },
  variants: {
    variant: {
      active: {
        color: "text.secondary",
      },
      inactive: {
        color: "text.tertiary",
      },
    },
  },
});
